import { DescriptionOutlined, FolderOffOutlined } from '@mui/icons-material';
import {
    Box,
    Button,
    CircularProgress,
    Divider,
    Skeleton,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { useMemo, useRef, type ChangeEvent, type FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import AttachToCaseDialog from './attachToCaseDialog';
import DocumentPreview from 'components/v2/common/documentPreview';
import Result from 'components/v2/common/Result';

import api from 'api';
import type { UserDocument } from 'api/rest/user_documents';
import { ACTIONS } from 'constant';
import { INITIAL_DOCS, hasResource } from 'helpers/initialDocuments';
import useInitialDocuments from 'hooks/useInitialDocuments';
import useModalObj from 'hooks/useModalObj';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import { RootState } from 'redux/store';
import type { InitialDocConfig, InitialDocKey } from 'types';
import { getErrorMessage } from 'utils/v2/errorHelper';

interface InitialDocumentsProps {
    userId?: number | string | null;
}

interface PreviewModalData {
    url: string;
    fileName: string;
    title: string;
}

interface CopyModalData {
    docId: number;
    label: string;
}

type ModalData = PreviewModalData | CopyModalData;

const isPreviewData = (data: ModalData | null): data is PreviewModalData =>
    Boolean(data && 'url' in data);

const isCopyData = (data: ModalData | null): data is CopyModalData =>
    Boolean(data && 'docId' in data);

const InitialDocuments: FC<InitialDocumentsProps> = ({ userId: userIdProp } = {}) => {
    const dispatch = useDispatch();
    const { translate } = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const user = useSelector((state: RootState) => state.user.entity);
    const selectedUserId = useSelector((state: RootState) => state.selectedAccount.selectedUserId);
    const applications = useSelector((state: RootState) => state.applications.applications);
    const selectedApplicationId = useSelector(
        (state: RootState) => state.selectedApplication.selectedApplication
    );

    const userId = userIdProp ?? selectedUserId ?? user?.id ?? null;
    const { docs, loading, error, uploading, viewing, providedCount, upload, getPreview } =
        useInitialDocuments(userId);

    const { modalObject, setModalObject, onResetModalObj } = useModalObj<ModalData>();

    const fileInputRefs = useRef<Record<InitialDocKey, HTMLInputElement | null>>({
        passport: null,
        resume: null,
        proofOfAddress: null,
    });

    const candidateCases = useMemo(() => {
        if (!userId) return [];
        const target = userId.toString();
        return (applications || []).filter((app) => app.candidate_id.toString() === target);
    }, [applications, userId]);

    const handleAction = (action: ACTIONS, cfg?: InitialDocConfig, doc?: UserDocument | null) => {
        switch (action) {
            case ACTIONS.PREVIEW_FILE:
                if (!cfg) return;
                void (async () => {
                    const preview = await getPreview(cfg);
                    if (!preview) {
                        dispatch(
                            setAlert({
                                message: getErrorMessage(null, translate('initialDocumentsError')),
                                type: 'error',
                            })
                        );
                        return;
                    }
                    setModalObject((prev) => ({
                        ...prev,
                        action,
                        data: {
                            url: preview.url,
                            fileName: preview.fileName,
                            title: translate(preview.titleKey),
                        },
                        loading: false,
                        disabled: false,
                    }));
                })();
                break;
            case ACTIONS.SELECT: {
                const docId = doc?.id;
                if (!cfg || docId == null) return;
                setModalObject((prev) => ({
                    ...prev,
                    action,
                    data: { docId, label: translate(cfg.labelKey) },
                    loading: false,
                    disabled: false,
                }));
                break;
            }
            default:
                break;
        }
    };

    const handleFileChange =
        (cfg: InitialDocConfig) => async (event: ChangeEvent<HTMLInputElement>) => {
            const file = event.target.files?.[0] ?? null;
            event.target.value = '';
            if (!file) return;

            const success = await upload(cfg, file);
            dispatch(
                setAlert({
                    message: translate(
                        success ? 'initialDocumentsUploaded' : 'initialDocumentsUploadFailed'
                    ),
                    type: success ? 'success' : 'error',
                })
            );
        };

    const handleConfirmCopy = async (applicationId: number) => {
        if (!isCopyData(modalObject.data)) return;

        setModalObject((prev) => ({ ...prev, loading: true, disabled: true }));
        try {
            await api.userDocuments.copyToApplication(modalObject.data.docId, applicationId);
            dispatch(
                setAlert({
                    message: translate('initialDocumentsCopied'),
                    type: 'success',
                })
            );
            onResetModalObj();
        } catch (copyError) {
            dispatch(
                setAlert({
                    message: getErrorMessage(copyError, translate('initialDocumentsCopyFailed')),
                    type: 'error',
                })
            );
            setModalObject((prev) => ({ ...prev, loading: false, disabled: false }));
        }
    };

    if (!userId) {
        return null;
    }

    return (
        <>
            <Box
                sx={{
                    width: '100%',
                    bgcolor: 'background.paper',
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    overflow: 'hidden',
                    p: { xs: 2, sm: 2.5 },
                }}
            >
                <Stack
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}
                >
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 0 }}>
                        <DescriptionOutlined color="primary" fontSize="small" />
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            {translate('initialDocuments')}
                        </Typography>
                    </Stack>
                    {!loading && !error ? (
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                            {providedCount} / {INITIAL_DOCS.length}
                        </Typography>
                    ) : null}
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {translate('initialDocumentsDescription')}
                </Typography>

                {loading ? (
                    <Stack spacing={1.5}>
                        {INITIAL_DOCS.map((cfg) => (
                            <Skeleton key={cfg.key} variant="rounded" height={40} />
                        ))}
                    </Stack>
                ) : error ? (
                    <Result
                        icon={<FolderOffOutlined sx={{ fontSize: 32 }} />}
                        title={translate('errorTitle')}
                        description={translate('initialDocumentsError')}
                        sx={{ py: 3 }}
                    />
                ) : (
                    <Stack divider={<Divider flexItem />} spacing={1.5}>
                        {INITIAL_DOCS.map((cfg) => {
                            const doc = docs[cfg.key];
                            const provided = hasResource(doc);
                            const isUploading = uploading[cfg.key];
                            const isOpeningPreview = viewing[cfg.key];
                            const label = translate(cfg.labelKey);

                            return (
                                <Stack
                                    key={cfg.key}
                                    direction={{ xs: 'column', sm: 'row' }}
                                    spacing={1}
                                    sx={{
                                        alignItems: { xs: 'stretch', sm: 'center' },
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <Typography variant="body2" sx={{ fontWeight: 600, py: 0.75 }}>
                                        {label}
                                    </Typography>

                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        sx={{
                                            alignItems: 'center',
                                            justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                                            flexWrap: 'wrap',
                                        }}
                                    >
                                        {provided ? (
                                            <>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    disabled={isOpeningPreview}
                                                    onClick={() =>
                                                        handleAction(ACTIONS.PREVIEW_FILE, cfg)
                                                    }
                                                    sx={{ textTransform: 'none' }}
                                                >
                                                    {isOpeningPreview ? (
                                                        <CircularProgress
                                                            size={16}
                                                            sx={{ mr: 1 }}
                                                        />
                                                    ) : null}
                                                    {translate('view')}
                                                </Button>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() =>
                                                        handleAction(ACTIONS.SELECT, cfg, doc)
                                                    }
                                                    sx={{ textTransform: 'none' }}
                                                >
                                                    {translate('attachToCase')}
                                                </Button>
                                            </>
                                        ) : null}
                                        <Button
                                            size="small"
                                            variant={provided ? 'outlined' : 'contained'}
                                            disabled={isUploading}
                                            onClick={() => fileInputRefs.current[cfg.key]?.click()}
                                            sx={{ textTransform: 'none' }}
                                        >
                                            {isUploading ? (
                                                <CircularProgress size={16} sx={{ mr: 1 }} />
                                            ) : null}
                                            {isUploading
                                                ? translate('uploading')
                                                : translate(provided ? 'replace' : 'upload')}
                                        </Button>
                                    </Stack>
                                </Stack>
                            );
                        })}
                    </Stack>
                )}
            </Box>

            {INITIAL_DOCS.map((cfg) => (
                <input
                    key={`file-input-${cfg.key}`}
                    ref={(el) => {
                        fileInputRefs.current[cfg.key] = el;
                    }}
                    type="file"
                    accept="application/pdf,image/*,.doc,.docx"
                    hidden
                    onChange={handleFileChange(cfg)}
                />
            ))}

            <AttachToCaseDialog
                open={modalObject.action === ACTIONS.SELECT}
                documentLabel={isCopyData(modalObject.data) ? modalObject.data.label : ''}
                cases={candidateCases}
                defaultCaseId={selectedApplicationId ? Number(selectedApplicationId) || null : null}
                loading={modalObject.loading}
                onClose={onResetModalObj}
                onConfirm={handleConfirmCopy}
            />

            <DocumentPreview
                open={
                    modalObject.action === ACTIONS.PREVIEW_FILE && isPreviewData(modalObject.data)
                }
                mobile={isMobile}
                onClose={onResetModalObj}
                fileUrl={isPreviewData(modalObject.data) ? modalObject.data.url : null}
                fileName={isPreviewData(modalObject.data) ? modalObject.data.fileName : undefined}
                title={isPreviewData(modalObject.data) ? modalObject.data.title : undefined}
            />
        </>
    );
};

export default InitialDocuments;
